// Concurrent-touch tracker for the map container.
//
// A draggable google.maps.Marker grabs a single finger that lands on it and
// starts dragging. That's what we want for a one-finger drag — but when the
// user is pinch-zooming (or two-finger panning) and one finger happens to land
// on a pin, the pin hijacks the gesture and moves instead of the MAP zooming.
//
// So we watch how many fingers are on the map surface. Two or more means the
// user is manipulating the map, not a pin: markers listen for that and yield
// (disable dragging, snap back any accidental drag). One finger still drags.

type Listener = (multi: boolean) => void;

let touchCount = 0;
let multi = false;
const listeners = new Set<Listener>();

function emit(): void {
  const next = touchCount >= 2;
  if (next === multi) return;
  multi = next;
  for (const l of listeners) l(multi);
}

/** True while two or more fingers are on the map (a pinch / two-finger pan). */
export function isMultiTouch(): boolean {
  return multi;
}

/** Subscribe to multi-touch on/off transitions. Returns an unsubscribe fn. */
export function onMultiTouchChange(l: Listener): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

let installed = false;

/**
 * Installs the touch listeners on the map container (once). Capture phase so we
 * see the second finger even if a marker/overlay stops propagation, and passive
 * since we only observe — we never call preventDefault on the map's own gestures.
 */
export function installTouchTracker(el: HTMLElement): void {
  if (installed) return;
  installed = true;
  const update = (e: TouchEvent) => {
    touchCount = e.touches.length;
    emit();
  };
  const opts = { capture: true, passive: true } as const;
  el.addEventListener('touchstart', update, opts);
  el.addEventListener('touchend', update, opts);
  el.addEventListener('touchcancel', update, opts);
}
